import type {
  TimeSeriesChartCommand,
  TimeSeriesChartState,
} from 'features/knowledgeDocumentViewer/components/advancedSearch/utils/documentSearchApiSchemas'
import type { EmbeddedTrendView } from 'features/timeSeries/computed/EmbeddedWorkbenchContext'
import { useCallback, useSyncExternalStore } from 'react'

import {
  applyChartCommand,
  applyWorkbenchView,
  chartStateFromPreview,
  sameChartView,
} from './chartCommands'
import {
  keepOpenWindowForMarker,
  mergeTrendPreviews,
  type TrendPreview,
} from './trendPreview'

/**
 * The trend preview beside each chat, keyed by the chat's tab.
 *
 * A module store rather than page state because the agent changes it from the
 * SSE stream (chart commands arrive while the page may be re-rendering or
 * hidden) and the next request has to report what the chart shows now.
 */
interface TrendPreviewEntry {
  preview: TrendPreview | null
  revision: number
}

const EMPTY: TrendPreviewEntry = { preview: null, revision: 0 }
const entries = new Map<string, TrendPreviewEntry>()
const listeners = new Map<string, Set<() => void>>()

export const DEFAULT_TREND_SCOPE = 'solo'

export function trendPreviewScope(tabId: string | undefined): string {
  return tabId ?? DEFAULT_TREND_SCOPE
}

function read(scope: string): TrendPreviewEntry {
  return entries.get(scope) ?? EMPTY
}

function write(scope: string, preview: TrendPreview | null): TrendPreviewEntry {
  const next = { preview, revision: read(scope).revision + 1 }
  entries.set(scope, next)
  listeners.get(scope)?.forEach((listener) => listener())
  return next
}

export function getTrendPreview(scope: string): TrendPreview | null {
  return read(scope).preview
}

export function getTrendChartState(
  scope: string
): TimeSeriesChartState | undefined {
  const entry = read(scope)
  return chartStateFromPreview(entry.preview, entry.revision)
}

export function setTrendPreview(
  scope: string,
  preview: TrendPreview | null
): void {
  if (read(scope).preview === preview) return
  write(scope, preview)
}

/** Open or fold a trend into the preview, the way a trend card click does. */
export function showTrendPreview(scope: string, incoming: TrendPreview): void {
  const current = read(scope).preview
  write(
    scope,
    current
      ? mergeTrendPreviews(current, keepOpenWindowForMarker(current, incoming))
      : { ...incoming, sessionKey: incoming.sessionKey ?? crypto.randomUUID() }
  )
}

/** Apply an agent chart command and return the state to acknowledge with. */
export function applyTrendChartCommand(
  scope: string,
  command: TimeSeriesChartCommand,
  fallbackLabel: string
): TimeSeriesChartState {
  const current = read(scope).preview
  const applied = applyChartCommand(current, command, fallbackLabel)
  const next = write(scope, {
    ...applied,
    sessionKey: applied.sessionKey ?? crypto.randomUUID(),
  })
  return (
    chartStateFromPreview(next.preview, next.revision) ?? {
      revision: next.revision,
    }
  )
}

/** Fold what the user did on the embedded chart into the preview; a no-op when nothing changed. */
export function syncTrendPreviewView(
  scope: string,
  view: EmbeddedTrendView
): void {
  const current = read(scope).preview
  if (!current) return
  const next = applyWorkbenchView(current, view)
  if (sameChartView(current, next)) return
  write(scope, next)
}

export function subscribeTrendPreview(
  scope: string,
  listener: () => void
): () => void {
  const set = listeners.get(scope) ?? new Set()
  set.add(listener)
  listeners.set(scope, set)
  return () => {
    set.delete(listener)
    if (set.size === 0) listeners.delete(scope)
  }
}

export function useTrendPreview(scope: string): TrendPreview | null {
  const subscribe = useCallback(
    (listener: () => void) => subscribeTrendPreview(scope, listener),
    [scope]
  )
  const snapshot = useCallback(() => read(scope).preview, [scope])
  return useSyncExternalStore(subscribe, snapshot, snapshot)
}

export function resetTrendPreviewStore(): void {
  entries.clear()
  listeners.clear()
}
